import { ChangeDetectorRef, Component, ViewEncapsulation, ViewChild, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { SAUTableModule } from '@some-angular-utils/table';
import { Urls } from '@/apiConnection/urls';
import { ApiService } from '@/apiConnection/ApiService';
import { Utils } from '@/utils/utils';
import { VerifactuPendingInvoice } from '../interfaces/VerifactuInvoiceInterface';
import { CustomModalComponent } from '@/components/custom-modal/custom-modal.component';

const STATUS_LABELS: Record<string, string | undefined> = {
  pending: 'Pendiente',
  error: 'Error',
};

@Component({
  imports: [
    CommonModule,
    SAUTableModule,
    CustomModalComponent,
  ],
  templateUrl: './verifactu-invoice-list.page.html',
  styleUrl: './verifactu-invoice-list.page.scss',
  encapsulation: ViewEncapsulation.None,
})
export class VerifactuInvoiceListPage {

  @ViewChild('sauTable')
  sauTable: { refresh: () => void; } | undefined;

  @ViewChild('numberTmpl', { static: true }) numberTmpl!: TemplateRef<any>;
  @ViewChild('clientTmpl', { static: true }) clientTmpl!: TemplateRef<any>;
  @ViewChild('totalTmpl', { static: true }) totalTmpl!: TemplateRef<any>;
  @ViewChild('statusTmpl', { static: true }) statusTmpl!: TemplateRef<any>;
  @ViewChild('actionsTmpl', { static: true }) actionsTmpl!: TemplateRef<any>;

  public url = Urls.urls.documentsPendingVerifactu;
  public configured = true;
  public statusLabels = STATUS_LABELS;
  public sendingIds = new Set<number>();
  public loadingXmlIds = new Set<number>();
  public showXmlModal = false;
  public xmlPreview = '';

  public headers = [
    { name: 'Fecha', key: 'date', type: 'date' },
    { name: 'Nº Factura', key: 'id_by_company' },
    { name: 'Cliente', key: 'client' },
    { name: 'Total', key: 'total' },
    { name: 'Estado', key: 'verifactu_status' },
    { name: 'Opciones', key: 'actions' },
  ];

  constructor(
    private apiService: ApiService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.apiService.getPetition(Urls.urls.company).subscribe({
      next: (data: any) => {
        this.configured = data?.list?.[0]?.verifactu_cert_configured === true;
        this.cdr.detectChanges();
      },
    });
  }

  isSending(item: VerifactuPendingInvoice): boolean {
    return this.sendingIds.has(item.id);
  }

  send(item: VerifactuPendingInvoice): void {
    if (this.isSending(item)) return;
    this.sendingIds.add(item.id);

    this.apiService.save(Urls.urls.documentsSendVerifactu, { ids: [item.id] }, 0).subscribe({
      next: () => {
        this.sendingIds.delete(item.id);
        Utils.notification.success('Factura enviada a Verifactu correctamente');
        this.sauTable?.refresh();
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.sendingIds.delete(item.id);
        Utils.notification.alert(Utils.extractErrorMessage(error) || 'Error al enviar la factura a Verifactu');
        this.sauTable?.refresh();
        this.cdr.detectChanges();
      },
    });
  }

  isLoadingXml(item: VerifactuPendingInvoice): boolean {
    return this.loadingXmlIds.has(item.id);
  }

  viewXml(item: VerifactuPendingInvoice): void {
    if (this.isLoadingXml(item)) return;
    this.loadingXmlIds.add(item.id);

    // responseType 'text' is required to read the raw XML body without Angular
    // trying (and failing) to JSON.parse it — but that also means ErrorInterceptor's
    // err.error arrives here as a raw string instead of the usual parsed object,
    // so error messages are extracted manually instead of via Utils.extractErrorMessage.
    this.http.get(`${Urls.urls.verifactuInvoices}/${item.id}/preview-xml`, { responseType: 'text' }).subscribe({
      next: (xml: string) => {
        this.loadingXmlIds.delete(item.id);
        this.xmlPreview = xml;
        this.showXmlModal = true;
        this.cdr.detectChanges();
      },
      error: (error: any) => {
        this.loadingXmlIds.delete(item.id);
        Utils.notification.alert(this.extractTextErrorMessage(error));
        this.cdr.detectChanges();
      },
    });
  }

  closeXmlModal(): void {
    this.showXmlModal = false;
    this.xmlPreview = '';
  }

  private extractTextErrorMessage(error: any): string {
    if (typeof error !== 'string') return 'No se pudo generar la vista previa del XML';

    try {
      return JSON.parse(error)?.errors || 'No se pudo generar la vista previa del XML';
    } catch {
      return 'No se pudo generar la vista previa del XML';
    }
  }
}
